import React from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import type { TPermissionAction } from '@/shared/features/permissions/actions';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { AddTagMenuItem } from '@/shared/features/tags/components/tag-select/add-tag-menu-item';
import {
  TTagPermissionEntity,
  TTagSelectProps,
} from '@/shared/features/tags/components/tag-select/tag-select.types';
import { useReadAllTagsQuery } from '@/shared/redux/api/assets/tags/tags-api';
import { ReadAllTagsQuery, TagFragment } from '@/shared/types/types.generated';

const TAG_PERMISSION_ACTIONS = {
  Device: { link: 'tag:linkDevice', unlink: 'tag:unlinkDevice' },
  Document: { link: 'tag:linkDocument', unlink: 'tag:unlinkDocument' },
  Gateway: { link: 'tag:linkGateway', unlink: 'tag:unlinkGateway' },
  License: { link: 'tag:linkLicense', unlink: 'tag:unlinkLicense' },
  Pipeline: { link: 'tag:linkPipeline', unlink: 'tag:unlinkPipeline' },
  Project: { link: 'tag:linkProject', unlink: 'tag:unlinkProject' },
  User: { link: 'tag:linkUser', unlink: 'tag:unlinkUser' },
  Vault: { link: 'tag:linkVault', unlink: 'tag:unlinkVault' },
} satisfies Record<
  TTagPermissionEntity,
  { link: TPermissionAction; unlink: TPermissionAction }
>;

export const TagSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  tagFilter,
  getItemId = (value) => value as string,
  createOption,
  disableTags = false,
  additionalOptions = [],
  onSelectChange,
  disableTagCreation = false,
  label,
  hint,
  entity,
  ...props
}: TTagSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TFieldValues>();
  const alreadySelectedTags = useWatch({ name, control });
  const { can, canAny } = usePermissionCheck();
  const tagPermissionActions = entity ? TAG_PERMISSION_ACTIONS[entity] : null;
  const isUpdateOperation = Boolean(alreadySelectedTags);
  const hasPermissionToCreate = can('tag:createTag');
  const shouldRenderTagsInput =
    !tagPermissionActions ||
    (isUpdateOperation
      ? canAny([tagPermissionActions.link, tagPermissionActions.unlink])
      : can(tagPermissionActions.link));

  const isSelectedTag = (tagName: string) =>
    Array.isArray(alreadySelectedTags) && alreadySelectedTags.includes(tagName);

  const createDefaultOption = (tag: TagFragment) => {
    const canUnlinkTag = tagPermissionActions
      ? can(tagPermissionActions.unlink, tag.name)
      : true;
    const disableSelectedTag = !canUnlinkTag && isSelectedTag(tag.name);
    const canLinkTag = tagPermissionActions
      ? can(tagPermissionActions.link, tag.name)
      : true;
    const disableUnselectedTag = !canLinkTag && !isSelectedTag(tag.name);

    return {
      label: tag.name,
      value: tag.name,
      disabled: disableSelectedTag || disableUnselectedTag,
    } as TSelectOption<TOptionShape>;
  };

  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadAllTagsQuery) => {
    if (!data) {
      return [];
    }
    const tags = data.readAllTags.items;
    const validTags = tagFilter ? tags.filter((tag) => tagFilter(tag)) : tags;

    const options = validTags.map((tag) => _createOption(tag));

    return [...additionalOptions, ...options];
  };

  const _label = label ?? t('tags:tag_select.label');
  const placeholder =
    disableTagCreation || !hasPermissionToCreate
      ? undefined
      : t('tags:tag_select.placeholder');

  const getRenderNoOptions = (
    inputValue: string,
    resetInputValue: () => void,
  ) => {
    if (disableTagCreation || !hasPermissionToCreate) {
      return undefined;
    }

    return (
      <AddTagMenuItem
        name={inputValue}
        onSuccess={(createdTag) => {
          resetInputValue();
          const currentTags = alreadySelectedTags || [];
          const updatedTags = [...currentTags, createdTag];
          setValue(name, updatedTags as TFieldValues[typeof name]);
        }}
      />
    );
  };

  if (!shouldRenderTagsInput) {
    return null;
  }

  return (
    <AsyncSearchableSelect<TFieldValues, ReadAllTagsQuery, TOptionShape>
      {...props}
      name={name}
      label={_label}
      placeholder={placeholder}
      disableTags={disableTags}
      multiple={true}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadAllTagsQuery,
        transformData,
      }}
      onSelectChange={onSelectChange}
      hint={hint}
      renderNoOptions={getRenderNoOptions}
      enableTagsTooltip
    />
  );
};
